import { useMarketCardApolloThrottle } from "./useMarketCardApolloThrottle";
import { useMarketCardExchangeApolloThrottle } from "./useMarketCardExchangeApolloThrottle";
import { useIsExchangeMarketCard } from "./useIsExchangeMarketCard";
import { useGridCardApolloThrottle } from "./useGridCardApolloThrottle";

/**
 * Whether a pebble's card renders its migrated Apollo version rather than its legacy Redux entry: one switch
 * per migrated typename, read by both PebbleContent platforms so a migration adds its switch here once. A
 * typename missing here renders legacy rather than going live unswitchable. Gated on the typename rather
 * than on the registry, so a migrated card added later doesn't inherit another card's switch.
 *
 * - MarketCard: MARKET_CARD_APOLLO ships active in every environment, so the migrated card is normally what
 *   renders. Exchange needs MARKET_CARD_APOLLO_EXCHANGE as well: the card is far more heavily used there, so
 *   that product validates on its own schedule and rolls out after sportsbook. Absent means off, so exchange
 *   falls back to legacy wherever the second throttle has not been configured.
 * - GridCard: GRID_CARD_APOLLO, absent meaning off.
 *
 * Every throttle is read whatever the typename, so the hooks run in the same order on every render.
 */
export const useMigratedPebbleGate = (urn: string, typename: string): boolean => {
  const isMarketCardApolloActive = useMarketCardApolloThrottle();
  const isExchangeMarketCardApolloActive = useMarketCardExchangeApolloThrottle();
  const isExchangeCard = useIsExchangeMarketCard(urn);
  const isGridCardApolloActive = useGridCardApolloThrottle();

  const gateByTypename: Record<string, boolean> = {
    MarketCard: isMarketCardApolloActive && (!isExchangeCard || isExchangeMarketCardApolloActive),
    GridCard: isGridCardApolloActive,
  };

  // Own keys only, so a typename naming an Object prototype member (`constructor`) reads as unswitched.
  return Object.prototype.hasOwnProperty.call(gateByTypename, typename) ? gateByTypename[typename] : false;
};
